import React, { useState, useEffect } from 'react';
import { 
  Search, Plus, Edit, Trash2, Eye, Loader, X, AlertCircle, CheckCircle,
  RefreshCw, Shield, Key, Users, UserCheck, UserX, Settings, 
  Save, CheckSquare, Square, Filter
} from 'lucide-react';
import adminService, { 
  Role, Permission, CreateRoleRequest, UpdateRoleRequest,
  CreatePermissionRequest, RolePermissionsResponse, AdminRolesResponse,
  AssignPermissionsToRoleRequest, AssignRoleToAdminRequest, RemoveRoleFromAdminRequest,
  AdminProfile
} from '../../services/admin/adminService';
import toast from 'react-hot-toast';

type TabType = 'roles' | 'permissions' | 'role-permissions' | 'admin-roles';

const RolesPermissionsManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('roles');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Roles
  const [roles, setRoles] = useState<Role[]>([]);
  const [roleModal, setRoleModal] = useState<'create' | 'edit' | null>(null);
  const [deleteRoleModal, setDeleteRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [roleForm, setRoleForm] = useState<CreateRoleRequest>({
    role_name: '',
    description: '',
    permissions: [],
  });

  // Permissions
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [permissionModal, setPermissionModal] = useState<'create' | null>(null);
  const [permissionForm, setPermissionForm] = useState<CreatePermissionRequest>({
    permission_name: '',
    resource: '',
    action: 'read',
    description: '',
  });

  // Role Permissions
  const [rolePermissions, setRolePermissions] = useState<RolePermissionsResponse | null>(null);
  const [selectedRoleForPermissions, setSelectedRoleForPermissions] = useState<number | null>(null);
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);

  // Admin Roles
  const [adminList, setAdminList] = useState<AdminProfile[]>([]);
  const [selectedAdminId, setSelectedAdminId] = useState<number | null>(null);
  const [adminRoles, setAdminRoles] = useState<AdminRolesResponse | null>(null);
  const [assignRoleModal, setAssignRoleModal] = useState(false);
  const [removeRoleModal, setRemoveRoleModal] = useState(false);
  const [roleToAssign, setRoleToAssign] = useState<number | null>(null);
  const [roleToRemove, setRoleToRemove] = useState<number | null>(null);

  const [processing, setProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
    fetchAdmins();
  }, []);

  useEffect(() => {
    if (activeTab === 'role-permissions' && selectedRoleForPermissions) {
      fetchRolePermissions(selectedRoleForPermissions);
    }
    if (activeTab === 'admin-roles' && selectedAdminId) {
      fetchAdminRoles(selectedAdminId);
    }
  }, [activeTab, selectedRoleForPermissions, selectedAdminId]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const data = await adminService.getAllRoles();
      setRoles(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const data = await adminService.getAllPermissions();
      setPermissions(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch permissions');
    }
  };

  const fetchAdmins = async () => {
    try {
      // We'll need to get admin list - for now, we'll use a placeholder
      // In a real scenario, you might need an endpoint to get all admins
      const currentAdmin = await adminService.getProfile();
      setAdminList([currentAdmin]);
    } catch (error: any) {
      console.error('Failed to fetch admins:', error);
    }
  };

  const fetchRolePermissions = async (roleId: number) => {
    try {
      setLoading(true);
      const data = await adminService.getRolePermissions(roleId);
      setRolePermissions(data);
      setSelectedPermissions(data.permissions.map(p => p.permission_id));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch role permissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminRoles = async (adminId: number) => {
    try {
      setLoading(true);
      const data = await adminService.getAdminRoles(adminId);
      setAdminRoles(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch admin roles');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setProcessing(true);
      await adminService.createRole(roleForm);
      toast.success('Role created successfully!');
      setRoleModal(null);
      resetRoleForm();
      fetchRoles();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create role');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRole) return;
    
    try {
      setProcessing(true);
      const updateData: UpdateRoleRequest = {
        role_id: selectedRole.role_id,
        role_name: roleForm.role_name,
        description: roleForm.description || null,
      };
      await adminService.updateRole(updateData);
      toast.success('Role updated successfully!');
      setRoleModal(null);
      resetRoleForm();
      fetchRoles();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update role');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!selectedRole) return;
    
    try {
      setProcessing(true);
      await adminService.deleteRole(selectedRole.role_id);
      toast.success('Role deleted successfully!');
      setDeleteRoleModal(false);
      setSelectedRole(null);
      fetchRoles();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete role');
    } finally {
      setProcessing(false);
    }
  };

  const handleCreatePermission = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setProcessing(true);
      await adminService.createPermission(permissionForm);
      toast.success('Permission created successfully!');
      setPermissionModal(null);
      resetPermissionForm();
      fetchPermissions();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create permission');
    } finally {
      setProcessing(false);
    }
  };

  const handleAssignPermissions = async () => {
    if (!selectedRoleForPermissions) return;
    
    try {
      setProcessing(true);
      await adminService.assignPermissionsToRole({
        role_id: selectedRoleForPermissions,
        permission_ids: selectedPermissions,
      });
      toast.success('Permissions assigned successfully!');
      fetchRolePermissions(selectedRoleForPermissions);
      fetchRoles();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to assign permissions');
    } finally {
      setProcessing(false);
    }
  };

  const handleAssignRoleToAdmin = async () => {
    if (!selectedAdminId || !roleToAssign) return;
    
    try {
      setProcessing(true);
      await adminService.assignRoleToAdmin({
        target_admin_id: selectedAdminId,
        role_id: roleToAssign,
      });
      toast.success('Role assigned successfully!');
      setAssignRoleModal(false);
      setRoleToAssign(null);
      fetchAdminRoles(selectedAdminId);
      fetchRoles();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to assign role');
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveRoleFromAdmin = async () => {
    if (!selectedAdminId || !roleToRemove) return;
    
    try {
      setProcessing(true);
      await adminService.removeRoleFromAdmin({
        target_admin_id: selectedAdminId,
        role_id: roleToRemove,
      });
      toast.success('Role removed successfully!');
      setRemoveRoleModal(false);
      setRoleToRemove(null);
      fetchAdminRoles(selectedAdminId);
      fetchRoles();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to remove role');
    } finally {
      setProcessing(false);
    }
  };

  const openEditRole = (role: Role) => {
    setSelectedRole(role);
    setRoleForm({
      role_name: role.role_name,
      description: role.description || '',
      permissions: [],
    });
    setRoleModal('edit');
  };

  const openDeleteRole = (role: Role) => {
    setSelectedRole(role);
    setDeleteRoleModal(true);
  };

  const resetRoleForm = () => {
    setRoleForm({
      role_name: '',
      description: '',
      permissions: [],
    });
    setSelectedRole(null);
  };

  const resetPermissionForm = () => {
    setPermissionForm({
      permission_name: '',
      resource: '',
      action: 'read',
      description: '',
    });
  };

  const togglePermission = (permissionId: number) => {
    setSelectedPermissions(prev => 
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const filteredRoles = roles.filter(role =>
    role.role_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPermissions = permissions.filter(permission =>
    permission.permission_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    permission.resource.toLowerCase().includes(searchTerm.toLowerCase()) ||
    permission.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const actionOptions: Array<'create' | 'read' | 'update' | 'delete' | 'approve' | 'reject' | 'suspend' | 'manage'> = [
    'create', 'read', 'update', 'delete', 'approve', 'reject', 'suspend', 'manage'
  ];

  if (loading && activeTab === 'roles') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">Roles & Permissions</h1>
          <p className="text-secondary-600">Manage roles, permissions, and access control</p>
        </div>
        <button
          onClick={() => {
            fetchRoles();
            fetchPermissions();
            if (selectedRoleForPermissions) fetchRolePermissions(selectedRoleForPermissions);
            if (selectedAdminId) fetchAdminRoles(selectedAdminId);
          }}
          disabled={refreshing}
          className="flex items-center space-x-2 px-4 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all disabled:opacity-50"
        >
          <RefreshCw size={20} className={refreshing ? 'animate-spin' : ''} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        <div className="border-b border-secondary-200">
          <div className="flex overflow-x-auto">
            <button
              onClick={() => setActiveTab('roles')}
              className={`flex items-center space-x-2 px-6 py-4 font-semibold transition-all border-b-2 whitespace-nowrap ${
                activeTab === 'roles'
                  ? 'border-primary-500 text-primary-600 bg-primary-50'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
              }`}
            >
              <Shield size={20} />
              <span>Roles</span>
            </button>
            <button
              onClick={() => setActiveTab('permissions')}
              className={`flex items-center space-x-2 px-6 py-4 font-semibold transition-all border-b-2 whitespace-nowrap ${
                activeTab === 'permissions'
                  ? 'border-primary-500 text-primary-600 bg-primary-50'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
              }`}
            >
              <Key size={20} />
              <span>Permissions</span>
            </button>
            <button
              onClick={() => setActiveTab('role-permissions')}
              className={`flex items-center space-x-2 px-6 py-4 font-semibold transition-all border-b-2 whitespace-nowrap ${
                activeTab === 'role-permissions'
                  ? 'border-primary-500 text-primary-600 bg-primary-50'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
              }`}
            >
              <Settings size={20} />
              <span>Role Permissions</span>
            </button>
            <button
              onClick={() => setActiveTab('admin-roles')}
              className={`flex items-center space-x-2 px-6 py-4 font-semibold transition-all border-b-2 whitespace-nowrap ${
                activeTab === 'admin-roles'
                  ? 'border-primary-500 text-primary-600 bg-primary-50'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
              }`}
            >
              <Users size={20} />
              <span>Admin Roles</span>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Roles Tab */}
          {activeTab === 'roles' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search roles..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <button
                  onClick={() => {
                    resetRoleForm();
                    setRoleModal('create');
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-all"
                >
                  <Plus size={20} />
                  <span>Create Role</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredRoles.length === 0 ? (
                  <div className="col-span-full text-center py-12">
                    <AlertCircle className="mx-auto text-secondary-400 mb-4" size={48} />
                    <p className="text-secondary-600 font-medium">No roles found</p>
                  </div>
                ) : (
                  filteredRoles.map((role) => (
                    <div key={role.role_id} className="bg-white border-2 border-secondary-200 rounded-xl p-6 hover:border-primary-300 transition-all">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-secondary-900 mb-1">{role.role_name}</h3>
                          {role.description && (
                            <p className="text-sm text-secondary-600">{role.description}</p>
                          )}
                        </div>
                        {role.is_active === 1 ? (
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">Active</span>
                        ) : (
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">Inactive</span>
                        )}
                      </div>
                      <div className="flex items-center justify-between text-sm text-secondary-600 mb-4">
                        <span>{role.assigned_admins_count || 0} Admins</span>
                        <span>{role.permissions_count || 0} Permissions</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openEditRole(role)}
                          className="flex-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg font-semibold hover:bg-blue-100 transition-all text-sm"
                        >
                          <Edit size={16} className="inline mr-1" />
                          Edit
                        </button>
                        <button
                          onClick={() => openDeleteRole(role)}
                          className="flex-1 px-3 py-2 bg-red-50 text-red-600 rounded-lg font-semibold hover:bg-red-100 transition-all text-sm"
                        >
                          <Trash2 size={16} className="inline mr-1" />
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Permissions Tab */}
          {activeTab === 'permissions' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search permissions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <button
                  onClick={() => {
                    resetPermissionForm();
                    setPermissionModal('create');
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-all"
                >
                  <Plus size={20} />
                  <span>Create Permission</span>
                </button>
              </div>

              <div className="bg-white rounded-xl shadow-soft overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-secondary-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Permission</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Resource</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Action</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Description</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-secondary-700 uppercase">Assigned Roles</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-secondary-200">
                      {filteredPermissions.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-6 py-12 text-center text-secondary-600">
                            No permissions found
                          </td>
                        </tr>
                      ) : (
                        filteredPermissions.map((permission) => (
                          <tr key={permission.permission_id} className="hover:bg-secondary-50 transition-colors">
                            <td className="px-6 py-4">
                              <span className="font-semibold text-secondary-900">{permission.permission_name}</span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold capitalize">
                                {permission.resource}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold capitalize">
                                {permission.action}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-secondary-600">{permission.description || 'N/A'}</span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-secondary-600">{permission.assigned_roles_count || 0}</span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Role Permissions Tab */}
          {activeTab === 'role-permissions' && (
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Select Role</label>
                  <select
                    value={selectedRoleForPermissions || ''}
                    onChange={(e) => {
                      const roleId = e.target.value ? parseInt(e.target.value) : null;
                      setSelectedRoleForPermissions(roleId);
                      if (roleId) {
                        fetchRolePermissions(roleId);
                      }
                    }}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  >
                    <option value="">Select a role</option>
                    {roles.map(role => (
                      <option key={role.role_id} value={role.role_id}>{role.role_name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {selectedRoleForPermissions && rolePermissions && (
                <div className="space-y-4">
                  <div className="bg-primary-50 rounded-lg p-4">
                    <h3 className="text-lg font-bold text-secondary-900 mb-1">
                      {rolePermissions.role.role_name}
                    </h3>
                    {rolePermissions.role.description && (
                      <p className="text-sm text-secondary-600">{rolePermissions.role.description}</p>
                    )}
                  </div>

                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader className="animate-spin text-primary-500" size={32} />
                    </div>
                  ) : (
                    <>
                      <div className="bg-white rounded-xl shadow-soft p-6">
                        <h4 className="text-md font-bold text-secondary-900 mb-4">Select Permissions</h4>
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {permissions.map((permission) => (
                            <label
                              key={permission.permission_id}
                              className="flex items-center space-x-3 p-3 hover:bg-secondary-50 rounded-lg cursor-pointer"
                            >
                              {selectedPermissions.includes(permission.permission_id) ? (
                                <CheckSquare className="text-primary-500" size={20} />
                              ) : (
                                <Square className="text-secondary-400" size={20} />
                              )}
                              <div className="flex-1">
                                <p className="font-semibold text-secondary-900">{permission.permission_name}</p>
                                <p className="text-xs text-secondary-500">
                                  {permission.resource} • {permission.action}
                                </p>
                                {permission.description && (
                                  <p className="text-xs text-secondary-600 mt-1">{permission.description}</p>
                                )}
                              </div>
                              <input
                                type="checkbox"
                                checked={selectedPermissions.includes(permission.permission_id)}
                                onChange={() => togglePermission(permission.permission_id)}
                                className="hidden"
                              />
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <button
                          onClick={handleAssignPermissions}
                          disabled={processing}
                          className="flex items-center space-x-2 px-6 py-2 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-all disabled:opacity-50"
                        >
                          <Save size={20} />
                          <span>{processing ? 'Saving...' : 'Save Permissions'}</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Admin Roles Tab */}
          {activeTab === 'admin-roles' && (
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Select Admin</label>
                  <select
                    value={selectedAdminId || ''}
                    onChange={(e) => {
                      const adminId = e.target.value ? parseInt(e.target.value) : null;
                      setSelectedAdminId(adminId);
                      if (adminId) {
                        fetchAdminRoles(adminId);
                      }
                    }}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  >
                    <option value="">Select an admin</option>
                    {adminList.map(admin => (
                      <option key={admin.admin_id} value={admin.admin_id}>
                        {admin.name} ({admin.email})
                      </option>
                    ))}
                  </select>
                </div>
                {selectedAdminId && (
                  <button
                    onClick={() => {
                      setRoleToAssign(null);
                      setAssignRoleModal(true);
                    }}
                    className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-all"
                  >
                    <UserCheck size={20} />
                    <span>Assign Role</span>
                  </button>
                )}
              </div>

              {selectedAdminId && adminRoles && (
                <div className="space-y-4">
                  <div className="bg-primary-50 rounded-lg p-4">
                    <h3 className="text-lg font-bold text-secondary-900 mb-1">
                      {adminRoles.admin.full_name || adminRoles.admin.email}
                    </h3>
                    <p className="text-sm text-secondary-600">{adminRoles.admin.email}</p>
                  </div>

                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader className="animate-spin text-primary-500" size={32} />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {adminRoles.roles.length === 0 ? (
                        <div className="col-span-full text-center py-12">
                          <AlertCircle className="mx-auto text-secondary-400 mb-4" size={48} />
                          <p className="text-secondary-600 font-medium">No roles assigned</p>
                        </div>
                      ) : (
                        adminRoles.roles.map((role) => (
                          <div key={role.role_id} className="bg-white border-2 border-secondary-200 rounded-xl p-6">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <h3 className="text-lg font-bold text-secondary-900 mb-1">{role.role_name}</h3>
                                {role.description && (
                                  <p className="text-sm text-secondary-600">{role.description}</p>
                                )}
                              </div>
                              {role.is_active === 1 ? (
                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">Active</span>
                              ) : (
                                <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">Inactive</span>
                              )}
                            </div>
                            <div className="text-xs text-secondary-500 mb-4">
                              Assigned: {new Date(role.assigned_at).toLocaleDateString()}
                              {role.assigned_by_name && (
                                <span className="block">By: {role.assigned_by_name}</span>
                              )}
                            </div>
                            <button
                              onClick={() => {
                                setRoleToRemove(role.role_id);
                                setRemoveRoleModal(true);
                              }}
                              className="w-full px-3 py-2 bg-red-50 text-red-600 rounded-lg font-semibold hover:bg-red-100 transition-all text-sm"
                            >
                              <UserX size={16} className="inline mr-1" />
                              Remove Role
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Role Modal */}
      {roleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-secondary-900">
                  {roleModal === 'create' ? 'Create Role' : 'Edit Role'}
                </h2>
                <button
                  onClick={() => {
                    setRoleModal(null);
                    resetRoleForm();
                  }}
                  className="text-secondary-500 hover:text-secondary-700"
                >
                  <X size={24} />
                </button>
              </div>
              <form onSubmit={roleModal === 'create' ? handleCreateRole : handleUpdateRole} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Role Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={roleForm.role_name}
                    onChange={(e) => setRoleForm({...roleForm, role_name: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    placeholder="Enter role name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Description</label>
                  <textarea
                    value={roleForm.description}
                    onChange={(e) => setRoleForm({...roleForm, description: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    rows={3}
                    placeholder="Enter role description"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setRoleModal(null);
                      resetRoleForm();
                    }}
                    className="px-6 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={processing}
                    className="px-6 py-2 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-all disabled:opacity-50"
                  >
                    {processing ? 'Saving...' : roleModal === 'create' ? 'Create Role' : 'Update Role'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Role Modal */}
      {deleteRoleModal && selectedRole && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="text-red-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-secondary-900">Delete Role</h2>
                  <p className="text-sm text-secondary-600">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-secondary-700 mb-6">
                Are you sure you want to delete the role <strong>"{selectedRole.role_name}"</strong>?
                {selectedRole.assigned_admins_count && selectedRole.assigned_admins_count > 0 && (
                  <span className="block mt-2 text-red-600">
                    Warning: This role is assigned to {selectedRole.assigned_admins_count} admin(s).
                  </span>
                )}
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setDeleteRoleModal(false);
                    setSelectedRole(null);
                  }}
                  className="px-6 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteRole}
                  disabled={processing}
                  className="px-6 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-all disabled:opacity-50"
                >
                  {processing ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Permission Modal */}
      {permissionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-secondary-900">Create Permission</h2>
                <button
                  onClick={() => {
                    setPermissionModal(null);
                    resetPermissionForm();
                  }}
                  className="text-secondary-500 hover:text-secondary-700"
                >
                  <X size={24} />
                </button>
              </div>
              <form onSubmit={handleCreatePermission} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Permission Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={permissionForm.permission_name}
                    onChange={(e) => setPermissionForm({...permissionForm, permission_name: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    placeholder="e.g., manage_businesses"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Resource <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={permissionForm.resource}
                    onChange={(e) => setPermissionForm({...permissionForm, resource: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    placeholder="e.g., business"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Action <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={permissionForm.action}
                    onChange={(e) => setPermissionForm({...permissionForm, action: e.target.value as any})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  >
                    {actionOptions.map(action => (
                      <option key={action} value={action}>{action}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">Description</label>
                  <textarea
                    value={permissionForm.description}
                    onChange={(e) => setPermissionForm({...permissionForm, description: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    rows={3}
                    placeholder="Enter permission description"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setPermissionModal(null);
                      resetPermissionForm();
                    }}
                    className="px-6 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={processing}
                    className="px-6 py-2 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-all disabled:opacity-50"
                  >
                    {processing ? 'Creating...' : 'Create Permission'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Assign Role to Admin Modal */}
      {assignRoleModal && selectedAdminId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-secondary-900">Assign Role</h2>
                <button
                  onClick={() => {
                    setAssignRoleModal(false);
                    setRoleToAssign(null);
                  }}
                  className="text-secondary-500 hover:text-secondary-700"
                >
                  <X size={24} />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Select Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={roleToAssign || ''}
                    onChange={(e) => setRoleToAssign(e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full px-4 py-2 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  >
                    <option value="">Select a role</option>
                    {roles.filter(role => 
                      !adminRoles?.roles.some(ar => ar.role_id === role.role_id)
                    ).map(role => (
                      <option key={role.role_id} value={role.role_id}>{role.role_name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => {
                      setAssignRoleModal(false);
                      setRoleToAssign(null);
                    }}
                    className="px-6 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAssignRoleToAdmin}
                    disabled={processing || !roleToAssign}
                    className="px-6 py-2 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-all disabled:opacity-50"
                  >
                    {processing ? 'Assigning...' : 'Assign Role'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove Role from Admin Modal */}
      {removeRoleModal && roleToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <UserX className="text-red-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-secondary-900">Remove Role</h2>
                  <p className="text-sm text-secondary-600">Remove this role from the admin</p>
                </div>
              </div>
              <p className="text-secondary-700 mb-6">
                Are you sure you want to remove this role from the admin?
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setRemoveRoleModal(false);
                    setRoleToRemove(null);
                  }}
                  className="px-6 py-2 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRemoveRoleFromAdmin}
                  disabled={processing}
                  className="px-6 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-all disabled:opacity-50"
                >
                  {processing ? 'Removing...' : 'Remove Role'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesPermissionsManagement;

